/**
 * Web Push (FCM) device registration for the installed PWA.
 *
 * Enabling push on a device means: ask the browser for notification
 * permission, obtain an FCM registration token (bound to the dedicated
 * `firebase-messaging-sw.js` service worker), and persist it under
 * `tenants/{tenantId}/users/{uid}/devices/{token}`. The Cloud Function
 * dispatcher reads that subcollection to fan a push out to every device, and
 * prunes a token when FCM reports it is no longer registered.
 *
 * Everything degrades gracefully: a browser without Notification/serviceWorker,
 * an unsupported messaging environment, or a missing VAPID key all resolve to
 * `"unsupported"` rather than throwing.
 */
import { getApp } from "firebase/app";
import { deleteDoc, doc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteToken, getMessaging, getToken, isSupported } from "firebase/messaging";
import { getEnv } from "./env";

export type PushEnableResult = "granted" | "denied" | "unsupported";

const SW_URL = "/firebase-messaging-sw.js";

function devicePath(tenantId: string, uid: string, token: string): string {
  return `tenants/${tenantId}/users/${uid}/devices/${token}`;
}

/** Feature-detect the APIs FCM web push needs in this browser. */
function pushApisAvailable(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator
  );
}

/**
 * Request permission and register this device for push. On success the token is
 * written to Firestore so the server can target it. Returns:
 *  - `"granted"`  permission given and token registered
 *  - `"denied"`   the user blocked notifications
 *  - `"unsupported"` the browser/env can't do web push, or no VAPID key is set
 */
export async function enablePushOnThisDevice(
  tenantId: string,
  uid: string,
): Promise<PushEnableResult> {
  if (!pushApisAvailable()) return "unsupported";
  if (!(await isSupported())) return "unsupported";

  const vapidKey = getEnv().firebase.vapidKey;
  if (!vapidKey) return "unsupported";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  const registration = await navigator.serviceWorker.register(SW_URL);
  const messaging = getMessaging(getApp("praesto"));
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!token) return "unsupported";

  await setDoc(doc(getFirestore(getApp("praesto")), devicePath(tenantId, uid, token)), {
    token,
    userAgent: navigator.userAgent,
    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  });

  return "granted";
}

/**
 * Unregister this device: delete the current FCM token (both from FCM and from
 * the user's `devices` subcollection). Best-effort and idempotent — calling it
 * on a device that was never registered is a no-op.
 */
export async function disablePushOnThisDevice(tenantId: string, uid: string): Promise<void> {
  if (!pushApisAvailable()) return;
  if (!(await isSupported())) return;

  const vapidKey = getEnv().firebase.vapidKey;
  if (!vapidKey) return;

  const registration = await navigator.serviceWorker.getRegistration(SW_URL);
  const messaging = getMessaging(getApp("praesto"));
  const token = await getToken(messaging, {
    vapidKey,
    ...(registration ? { serviceWorkerRegistration: registration } : {}),
  });
  if (!token) return;

  await deleteToken(messaging);
  await deleteDoc(doc(getFirestore(getApp("praesto")), devicePath(tenantId, uid, token)));
}

/**
 * The FCM token of the device the user is currently on, if push is already
 * enabled here (permission granted) — otherwise `null`. NEVER prompts. Used to
 * mark "this device" in the device list and reflect the enable toggle state.
 */
export async function currentDeviceToken(): Promise<string | null> {
  if (!pushApisAvailable()) return null;
  if (!(await isSupported())) return null;
  const vapidKey = getEnv().firebase.vapidKey;
  if (!vapidKey) return null;
  if (Notification.permission !== "granted") return null;
  try {
    const registration =
      (await navigator.serviceWorker.getRegistration(SW_URL)) ??
      (await navigator.serviceWorker.register(SW_URL));
    const messaging = getMessaging(getApp("praesto"));
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
    return token || null;
  } catch {
    return null;
  }
}
